// Shared types for the admin Time Card approval editor.
// The /api/time list endpoint returns richer rows than the base TimeEntry type
// (it joins employee + job names and exposes the needs_review flag), so we model
// those extra, optional fields here.

// Format decimal hours as H:MM (matches bfav5's formatHoursAsTime so the PWA and
// the bfav5 time card show the same value, e.g. 39.72 → "39:43").
export const formatHM = (hours: number): string => {
  const total = Math.round((Number(hours) || 0) * 60);
  const h = Math.floor(total / 60);
  const m = total % 60;
  return `${h}:${String(m).padStart(2, '0')}`;
};

export interface TimeEntryRow {
  entry_id: number;
  employee_id: number;
  job_id: number;
  clock_in: string;
  clock_out?: string | null;
  hours_worked?: number;
  lunch_taken?: boolean;
  lunch_minutes?: number;
  shop_time_minutes?: number;
  needs_review?: boolean;
  geofence_status?: string;        // 'inside' | 'outside' | 'unknown_job' | 'error'
  job_name?: string;
  first_name?: string;
  last_name?: string;
  manual_entry_reason?: string;
}

export interface PendingCorrection {
  correction_id: number;
  employee_id: number;
  entry_id?: number | null;
  week_start_date?: string;
  correction_type:
    | 'wrong_clock_in'
    | 'wrong_clock_out'
    | 'wrong_job'
    | 'missed_day'
    | 'did_not_take_lunch';
  first_name?: string;
  last_name?: string;
  original_clock_in?: string | null;
  original_clock_out?: string | null;
  original_job_name?: string | null;
  proposed_clock_in?: string | null;
  proposed_clock_out?: string | null;
  proposed_job_name?: string | null;
  proposed_job_id?: number | null;
  proposed_date?: string | null;
  employee_notes?: string | null;
  created_at?: string;
}

// Richer per-employee correction shape from GET /timecard-review/employee-status.
// Unlike /pending (which only returns pending), this returns ALL corrections for
// the review with their resolution state and joined job names/numbers.
export interface EmployeeCorrection {
  correction_id: number;
  entry_id?: number | null;
  correction_type: PendingCorrection['correction_type'];
  original_clock_in?: string | null;
  original_clock_out?: string | null;
  original_job_id?: number | null;
  original_job_name?: string | null;
  original_job_number?: string | null;
  proposed_clock_in?: string | null;
  proposed_clock_out?: string | null;
  proposed_job_id?: number | null;
  proposed_job_name?: string | null;
  proposed_job_number?: string | null;
  proposed_date?: string | null;
  employee_notes?: string | null;
  admin_status: 'pending' | 'approved' | 'rejected' | 'modified';
  admin_notes?: string | null;
  resolved_at?: string | null;
  hours_worked?: number | null;
  // joined from the linked TIME_ENTRIES row (which day this correction is for)
  entry_date?: string | null;       // EST date 'YYYY-MM-DD'
  full_clock_in?: string | null;
  full_clock_out?: string | null;
  // the entry's CURRENT job (original_job_id is a submit-time snapshot)
  entry_job_id?: number | null;
  entry_job_name?: string | null;
  entry_job_number?: string | null;
}

// 'resolved' = admin handled the last pending correction (needs the
// add-resolved-review-status.sql migration server-side).
export type ReviewStatusValue = 'pending' | 'approved' | 'issues_flagged' | 'skipped' | 'resolved';

// data payload of GET /timecard-review/employee-status (null when the employee
// never opened their Friday review for the week).
export interface EmployeeReviewStatus {
  review_status: ReviewStatusValue;
  reviewed_at?: string | null;
  skipped_count?: number;
  corrections: EmployeeCorrection[];
}
